import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'next-i18next';
import clsx from 'clsx';
import Button from '@components/atoms/button/button';
import useOnClickOutside from '@hooks/browser-events/use-on-click-outside';
import useOnKeyPress from '@hooks/browser-events/use-on-key-press';
import useWindowSize from '@hooks/use-window-size';
import { useFocusRestore } from '@hooks/a11y/use-focus-restore';
import Icon from '@components/atoms/icon/icon';
import styles from './hot-filter-dialog.module.scss';

type HotFilterDialogProps = {
  headerLabel: string;
  isDialogOpened: boolean;
  isClearCtaDisabled?: boolean;
  onClose: () => void;
  handleApplyChange: () => void;
  handleClear: () => void;
  offset?: 'left' | 'right';
  dataCy?: string;
  showCtaSection?: boolean;
  clickOutsideRef?: React.MutableRefObject<HTMLDivElement | undefined>;
  customClass?: string;
  hasChildDialog?: boolean;
};

const getDataCy = (base: string, suffix?: string) => (base ? `${base}${suffix ? `-${suffix}` : ''}` : undefined);

const HotFilterDialog: React.FCWithChildren<HotFilterDialogProps> = ({
  headerLabel,
  isDialogOpened,
  isClearCtaDisabled,
  onClose,
  handleApplyChange,
  handleClear,
  offset = 'right',
  dataCy,
  showCtaSection = true,
  children,
  customClass,
  // a ref which encompasses both the button and the dialog
  clickOutsideRef,
  // whether the dialog opens other dialogs
  hasChildDialog = false,
}) => {
  const { t } = useTranslation();
  const { isWiderThanMd } = useWindowSize();

  const dialogRef = useRef<HTMLDivElement>(null);
  const listRef = clickOutsideRef ?? dialogRef;
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Restore focus to filter button when dialog closes
  useFocusRestore(isDialogOpened);

  // Close on click outside
  useOnClickOutside(
    listRef,
    () => {
      if (isDialogOpened) {
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        timeoutRef.current = setTimeout(() => {
          onClose?.();
        }, 10);
      }
    },
    isDialogOpened,
  );

  // Close on focus outside (keyboard navigation to another filter)
  useEffect(() => {
    if (!isDialogOpened || !listRef.current) {
      return;
    }

    const handleFocusOut = (e: FocusEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement;
      // if the related target is inside another dialog, do nothing
      // since e.g. sizes filters opens another dialog
      // and we need to return focus to the triggering element
      if (hasChildDialog && relatedTarget?.closest('[role="dialog"]')) {
        return;
      }

      // If focus moved outside this filter's container, close the dialog
      if (relatedTarget && !listRef.current?.contains(relatedTarget)) {
        onClose?.();
      }
    };

    listRef.current.addEventListener('focusout', handleFocusOut);

    return () => {
      listRef.current?.removeEventListener('focusout', handleFocusOut);
    };
  }, [isDialogOpened, onClose]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const escPress = useOnKeyPress('Escape', listRef);

  useEffect(() => {
    if (escPress) {
      onClose?.();
    }
  }, [escPress]);

  return (
    <div
      className={clsx(styles.hotFilter, customClass)}
      id={`hot-filter-content-${headerLabel}`}
      aria-labelledby={`hot-filter-button-${headerLabel}`}
      role="region"
      ref={dialogRef}
    >
      <div
        className={clsx(styles.hotFilter__dialog, {
          [styles['hotFilter__dialog--open']]: isDialogOpened,
          [styles.hotFilter__dialog__offsetLeft]: offset === 'left',
          [styles.hotFilter__dialog__offsetRight]: offset === 'right',
        })}
        data-cy={getDataCy(dataCy)}
      >
        <div className={styles.hotFilter__dialog__header}>
          <span
            className={styles.hotFilter__dialog__header__name}
            data-cy={getDataCy(dataCy, 'header-name')}
          >
            {t(headerLabel)}
          </span>
          <Button
            data-cy={getDataCy(dataCy, 'header-close-button')}
            disableDefaultStyling
            aria-label={t('MODAL.BUTTON.CLOSE')}
            onClick={() => onClose()}
          >
            <Icon
              name="close-default"
              className={styles.hotFilter__dialog__header__close}
              ariaHidden
            />
            <span className="visuallyhidden">{t('MODAL.BUTTON.CLOSE')}</span>
          </Button>
        </div>
        {children}
        {showCtaSection && (
          <div className={styles.hotFilter__dialog__action}>
            <Button
              variant="secondary"
              size={!isWiderThanMd ? 'large' : 'medium'}
              onClick={handleClear}
              data-cy={getDataCy(dataCy, 'clear-button')}
              className={styles.hotFilter__dialog__action__button}
              disabled={isClearCtaDisabled}
            >
              {t('FAVORITE_PAGE.FILTER_CTA.CLEAR')}
            </Button>
            <Button
              variant="primary"
              size={!isWiderThanMd ? 'large' : 'medium'}
              onClick={() => handleApplyChange()}
              data-cy={getDataCy(dataCy, 'apply-button')}
              className={styles.hotFilter__dialog__action__button}
            >
              {t('FAVORITE_PAGE.FILTER_CTA.APPLY')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default HotFilterDialog;
